import { formatInt } from './numberFormat';

/** Shares per lot: every stock leg is split over the pairs in whole lots. */
export const PAIR_IMPORT_LOT = 100;

export interface PairSplitLeg {
    symbol: string;
    /** Shares of this leg in each trading pair. */
    perPair: number;
    /** perPair × k: what the pair claims. */
    claimed: number;
    /** What does not divide: stays at the broker, unclaimed. */
    unclaimed: number;
    /** Why this leg cannot be imported as typed; null when it can. */
    problem: string | null;
}

/**
 * The server's split (pair-import.split.ts): k pairs of one contract each, every stock leg at
 * floor(qty / k / 100) × 100 per pair. `free` / `freeFutures` undefined skip those checks.
 */
export function splitPairImport(
    legs: { symbol: string; qty: number; free?: number }[],
    k: number,
    freeFutures?: number,
): { legs: PairSplitLeg[]; problems: string[] } {
    const problems: string[] = [];
    if (!Number.isInteger(k) || k < 0) {
        return {
            legs: legs.map((l) => ({ symbol: l.symbol, perPair: 0, claimed: 0, unclaimed: l.qty, problem: null })),
            problems: ['the number of pairs must be a whole number of 0 or more'],
        };
    }
    if (freeFutures !== undefined && k > freeFutures) {
        problems.push(`${k} pair(s) need ${k} futures contract(s); only ${formatInt(freeFutures)} are free`);
    }
    const out = legs.map((l) => {
        const perPair = k === 0 ? 0 : Math.floor(l.qty / k / PAIR_IMPORT_LOT) * PAIR_IMPORT_LOT;
        let problem: string | null = null;
        if (l.free !== undefined && l.qty > l.free) {
            problem = `${l.symbol}: only ${formatInt(l.free)} share(s) are free`;
        } else if (k === 0 && l.qty > 0) {
            problem = `${l.symbol}: set at least 1 pair to import shares`;
        } else if (k > 0 && perPair < PAIR_IMPORT_LOT) {
            // the server refuses a leg under one lot per pair, a 0 leg included
            problem = `${l.symbol}: ${formatInt(l.qty)} share(s) over ${k} pair(s) is less than ${PAIR_IMPORT_LOT} per pair`;
        }
        if (problem) problems.push(problem);
        return { symbol: l.symbol, perPair, claimed: perPair * k, unclaimed: l.qty - perPair * k, problem };
    });
    return { legs: out, problems };
}
